import {
  SignUpLayoutContainer,
  WacSignUpModel,
} from "@atlassiansox/bxpkit-wac-sign-up";
import { AnalyticsListener } from "@atlaskit/analytics-next";
import React, { useEffect, useState } from "react";
import { IntlProvider } from "react-intl";
import { trackEvent } from "../../common/analytics";
import { ConsentHubClient } from "../../common/ConsentHubV2";
import { SegmentType } from "@atlassiansox/bxpkit-core";

const getLocale = async () => {
  const consentHub = new ConsentHubClient();
  return await consentHub.requestConfig();
};

// Combined Analytics Tracking
const analyticsTrack = {
  segment: trackEvent,
  newRelic: {
    action: () => {},
    attribute: () => {},
    trackAjax: () => {},
  },
};

export type AnalyticsPayload = {
  readonly schemaVersion: number;
  readonly event: string;
  readonly eventType: string;
  readonly eventComponent: string;
  readonly eventContainer: string;
  readonly eventColumn: string;
  readonly eventName: string;
  readonly label: string;
  readonly parentLabel: string;
  readonly uuid: string;
  readonly layout: string;
  readonly bundle: string;
  readonly product: string;
  readonly edition: string;
};

export type SendAnalyticsOptions = {
  readonly payload: AnalyticsPayload;
  readonly context: AnalyticsPayload[];
};

function sendAnalytics({ payload, context }: SendAnalyticsOptions) {
  const analyticsDataList = [...context, payload];
  const analyticsEvent = analyticsDataList.reduce(
    (mergedAnalyticsEvent, currentAnalyicsEvent) => {
      return { ...mergedAnalyticsEvent, ...currentAnalyicsEvent };
    },
    {}
  ) as AnalyticsPayload;
  trackEvent(analyticsEvent.eventName, analyticsEvent);
}

type LayoutType =
  | "default"
  | "standalone"
  | "standalone-v2"
  | "devops"
  | "devops-v2"
  | "first-impressions"
  | "first-impressions-v2";

export type FirstImpressionsAppImageUrlsType = {
  readonly heroImage: string;
  readonly socialProofImage?: string;
};

export type FirstImpressionsImageUrlsType = {
  readonly backgroundImage: string;
  readonly jiraSoftware: FirstImpressionsAppImageUrlsType;
  readonly jiraServiceDesk: FirstImpressionsAppImageUrlsType;
  readonly confluence: FirstImpressionsAppImageUrlsType;
  readonly jiraCore: FirstImpressionsAppImageUrlsType;
  readonly teamCentral: FirstImpressionsAppImageUrlsType;
  readonly jiraProductDiscovery: FirstImpressionsAppImageUrlsType;
  readonly compass: FirstImpressionsAppImageUrlsType;
  readonly bitbucket: FirstImpressionsAppImageUrlsType;
};

export type LabelsType = { [key: string]: string };

export type Product = {
  readonly productKey: string;
  readonly productName: string;
  readonly productLogoUrl?: string;
  readonly productLogoWhiteUrl?: string;
};

type LocaleType = {
  readonly locale: string;
  readonly localeRequiresMarketingCommunicationOptIn: boolean;
};

type AnalyticsTrackType = {
  readonly segment: SegmentType;
};

export type ProductType = {
  readonly productKey: string;
  readonly productName: string;
  readonly productLogoUrl?: string;
  readonly productLogoWhiteUrl?: string;
  readonly atlasKitLogoName?: string;
};

export type BundleContent = {
  readonly heroImage: string;
  readonly headline: string;
  readonly socialProofImage?: string;
  readonly featureList: string;
};

export type ProductDataType = {
  readonly products: ProductType[];
  readonly bundleProductKeys: string;
  readonly iconSrc?: string;
  readonly iconAlt?: string;
  readonly calloutRichText?: string;
  readonly bundleContent?: {
    readonly default: BundleContent;
  };
};

export type DevopsImageUrlsType = {
  readonly sidebarImage: string;
  readonly backgroundImage: string;
  readonly envelopeN2N: string;
  readonly successN2E: string;
};

export type SimpleSignupImageUrlsType = {
  readonly atlassianLogoImage: string;
  readonly jswMobileNavImage: string;
};

export type FeatureFlags = {
  readonly enableTintinMvp?: boolean;
  readonly enableDevopsGoogleSignup?: boolean;
  readonly provisionSecondPartyApps?: boolean;
  readonly enableDeferredFullName?: boolean;
  readonly enableDeferredSiteName?: boolean;
  readonly enableDefaultExpandedEmail?: boolean;
  readonly enableUseCodeAWSContent?: boolean;
  readonly enableCutSignUpStepsInHalf?: boolean;
  readonly enableCutOnboardingStepsInHalf?: boolean;
  readonly enableReverseTrial: string;
};

export type GlobalConfiguration = {
  readonly confirmationPageUrl: string;
  readonly checkEmailPageUrl: string;
  readonly socialContinueUrl: string;
  readonly siteRenamePageUrl?: string;
};

export type SignUpLayoutContainerProps = {
  readonly allowEnableAutogenSiteName: boolean;
  readonly allowSignupWithGoogle: boolean;
  readonly allowSignupWithAllSocialOptions: boolean;
  readonly enableFreeBreachTest: boolean;
  readonly showSocialSignupForm: boolean;
  readonly socialContinueUrl: string;
  readonly loadingImageSrc: string;
  readonly errorImageSrc: string;
  readonly productData: { [key: string]: ProductDataType };
  readonly locale: LocaleType;
  readonly analyticsTrack: AnalyticsTrackType;
  readonly labels: LabelsType;
  readonly layout?: LayoutType;
  readonly productOverride?: string;
  readonly customSignupContext?: string;
  readonly bundleOverride?: string;
  readonly editionOverride?: string;
  readonly performanceStartTime?: number;
  readonly firstImpressionsImageUrls: FirstImpressionsImageUrlsType;
  readonly devopsImageUrls: DevopsImageUrlsType;
  readonly simpleSignupImageUrls: SimpleSignupImageUrlsType;
  readonly siteRenamePageUrl?: string;
  readonly enableSkipRecaptcha?: boolean;
  readonly featureFlags: FeatureFlags;
  readonly globalConfiguration?: GlobalConfiguration;
};

/**
 * @param props - same as the props received by this container
 */
export default function WacSignUpLayoutContainer(
  props: SignUpLayoutContainerProps
) {
  const [locale, setLocale] = useState({
    locale: "UNKNOWN",
    localeRequiresMarketingCommunicationOptIn: true,
  });
  const [fetchingLocale, setFetchingLocale] = useState(true);

  useEffect(() => {
    async function getLocaleData() {
      const localeData = await getLocale();
      if (localeData) {
        setLocale(localeData);
      }
    }
    getLocaleData()
      .then(() => setFetchingLocale(false))
      .catch(() => setFetchingLocale(false));
  }, []);

  return fetchingLocale ? (
    <div></div>
  ) : (
    <AnalyticsListener onEvent={sendAnalytics}>
      <IntlProvider
        locale={document.documentElement.lang}
        messages={props.labels}
      >
        <SignUpLayoutContainer
          {...props}
          locale={locale}
          analyticsTrack={analyticsTrack}
          performanceStartTime={performance.now()}
        />
      </IntlProvider>
    </AnalyticsListener>
  );
}
